import React from 'react';

export const OTP_LENGTH = 6;
export const EMPTY_OTP = Array(OTP_LENGTH).fill('');

// m:ss for OTP expiry countdowns.
export const formatCountdown = (ms) => {
  const total = Math.max(Math.ceil(ms / 1000), 0);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const focusOtpBox = (inputsRef, index) => {
  const box = inputsRef.current[Math.min(Math.max(index, 0), OTP_LENGTH - 1)];
  if (box) {
    box.focus();
    box.select();
  }
};

// Six single-digit boxes (typing, paste and autofill of formatted codes).
// Shared by two-step login verification and forgot password. The parent owns
// `digits` (array of OTP_LENGTH strings) and `inputsRef`, so it can clear
// and refocus the boxes; `onEdit` fires whenever digits are entered.
const OtpInput = ({ digits, setDigits, inputsRef, disabled = false, hasError = false, onEdit }) => {
  const focusBox = (index) => focusOtpBox(inputsRef, index);

  // Functional updates: several keystrokes can land before React re-renders,
  // so building from the `digits` closure would drop characters.
  const setDigitsAt = (start, chars) => {
    setDigits((prev) => {
      const next = [...prev];
      chars.forEach((d, i) => { next[start + i] = d; });
      return next;
    });
  };

  // Spreads a run of digits (typed or pasted) across the boxes from `start`.
  const fillFrom = (start, value) => {
    const incoming = value.replace(/\D/g, '').slice(0, OTP_LENGTH - start);
    if (!incoming) return;
    setDigitsAt(start, incoming.split(''));
    if (onEdit) onEdit();
    focusBox(start + incoming.length);
  };

  const handleChange = (index, e) => {
    const value = e.target.value;
    if (value === '') {
      setDigitsAt(index, ['']);
      return;
    }
    // Non-digits are simply ignored; a box never shows a letter or symbol.
    // Typing into an already-filled box yields "old+new" (or "new+old"):
    // keep only the new digit. Anything longer is a paste/autofill.
    const cleaned = value.replace(/\D/g, '');
    const prev = digits[index];
    let incoming = cleaned;
    if (prev && cleaned.length === 2) {
      if (cleaned.startsWith(prev)) incoming = cleaned.slice(1);
      else if (cleaned.endsWith(prev)) incoming = cleaned.slice(0, 1);
    }
    fillFrom(index, incoming);
  };

  const handleKeyDown = (index, e) => {
    // Block non-digit characters before they reach the input; filtering only
    // in onChange lets a fast next keystroke get wiped by React's value reset.
    if (e.key.length === 1 && !/\d/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
      e.preventDefault();
      setDigitsAt(index - 1, ['']);
      focusBox(index - 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    fillFrom(index, e.clipboardData.getData('text').trim());
  };

  return (
    <div className="otp-boxes" role="group" aria-label="6-digit verification code">
      <style>{`
        .otp-boxes {
          display: flex;
          gap: 0.6rem;
          justify-content: center;
          margin-bottom: 0.75rem;
        }
        .otp-box {
          width: 3rem;
          height: 3.4rem;
          text-align: center;
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          border: 1px solid #d1d5db;
          border-radius: 0.6rem;
          background: white;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .otp-box:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
        }
        .otp-box-error { border-color: #dc2626; background: #fef2f2; }
        .otp-box:disabled { background: #f3f4f6; color: #9ca3af; }
        @media (max-width: 480px) {
          .otp-boxes { gap: 0.4rem; }
          .otp-box { width: 2.6rem; height: 3rem; font-size: 1.3rem; }
        }
      `}</style>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputsRef.current[index] = el; }}
          className={`otp-box${hasError ? ' otp-box-error' : ''}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          // No maxLength: autofill/paste can arrive as one input event
          // ("482 731"), which maxLength would truncate before
          // fillFrom strips the non-digits.
          value={digit}
          disabled={disabled}
          aria-label={`Digit ${index + 1}`}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};

export default OtpInput;
